import { ProjectPricingItem, FAQItem, OnboardingStep, CompanyTraction } from "./types";

export const TRACTION_DATA: CompanyTraction[] = [
  {
    name: "Enterprise AI Memory & Compliance",
    description: "Graph-based memory, case-law mapping, compliance reasoning, and auditable AI architecture."
  },
  {
    name: "FinTech & Quantitative Infrastructure",
    description: "High-volume data pipelines, graph validation layers, and risk-aware AI systems for regulated financial environments."
  },
  {
    name: "AI Security & Runtime Governance",
    description: "Zero-trust gateways, deterministic execution controls, prompt-injection defenses, and data-leak containment."
  },
  {
    name: "Board & Executive Advisory",
    description: "Strategic AI guidance, settlement infrastructure, cryptographic routing, and executive decision support."
  }
];

export const PRICING_DATA: ProjectPricingItem[] = [
  {
    component: "Foundational Architecture Review",
    investment: "$15,000",
    investmentSubtext: "(FIXED ONE-TIME FEE)",
    terms: "Complete 14-day technical discovery, deep system component profiling, unmanaged \"Shadow AI\" leak assessment, and an absolute 30/60/90 day execution blueprint. Paid 100% upfront."
  },
  {
    component: "Fractional CAIO Retainer",
    investment: "$25,000 / month",
    investmentSubtext: "(3-MONTH MINIMUM)",
    terms: "Continuous strategic portfolio governance, foundation-model vendor matrix steering, automated validation layer alignment, and board-level risk tracking. May be canceled at anytime after 3 months with 30 days notice."
  },
  {
    component: "Target Annualized AI Budget",
    investment: "$500,000 – $10,000,000",
    terms: "Optimized for mid-market operations where introducing an elite, part-time technical executive prevents failed experiment waste and unmanaged liabilities."
  }
];

export const FAQ_DATA: FAQItem[] = [
  {
    question: "When should a company hire a fractional vs. full-time CAIO?",
    answer: "A fractional CAIO is ideal when your organization is early in its AI maturation lifecycle, has fewer than three concurrent use cases active in production, or requires clear operational validation before expanding permanent headcount. Transition to a permanent full-time CAIO becomes logical once AI forms your core product, or over 5% of total revenue relies strictly on proprietary models."
  },
  {
    question: "How do AI budget triggers affect the CAIO hiring decision?",
    answer: "When your annualized AI spend sits between $100K and $1M, a fractional CAIO provides the most capital-efficient architecture loop to minimize failed experimentation spend. However, once your internal infrastructure or resource commitments cross the $1M–$2M/year threshold, it demands independent full-time C-suite stewardship to prevent massive cloud-cost inflation."
  },
  {
    question: "How do fractional CAIOs differ from AI consultants and CTOs?",
    answer: "An AI consultant operates externally, delivering time-boxed slide decks and departing without holding operational risk. A fractional CTO owns your broad technology stack. In contrast, an embedded fractional CAIO unifies both strategy and data architecture—taking corporate accountability for outcomes, model selection risks, and board-level risk tracking metrics."
  },
  {
    question: "What is your explicit posture on AI safety and security?",
    answer: "Traditional cybersecurity cannot defend against modern data-layer vulnerabilities. I mandate a strict secure-by-design runtime framework: Custom markup parsing (like enhanced ChatML) to block adversarial prompt injection, dynamic API gateway gating (RBAC/ABAC) to limit autonomous functionalities, and privacy proxies or isolated VPC enclaves to protect IP and eliminate unmanaged shadow AI leaks."
  },
  {
    question: "What are the operational parameters for executive availability?",
    answer: "Engagements operate on an outcome-driven executive cadence: active internal communication lines with a direct response to operational Slack/Teams inquiries within a 2-hour window, weekly 60-minute syncs, and under 24-hour rapid response video staging for emergency board escalations. On-site presence is provided across the West Coast region, including British Columbia."
  }
];

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    step: "STEP 1",
    title: "Initial Inquiry",
    description: "Submit the secure intake form with your organization profile, AI goals, existing systems, data boundaries, and operating constraints.",
    details: "Initial fit review and recommended next step.",
    status: "Intake Active"
  },
  {
    step: "STEP 2",
    title: "Diagnostic Screening",
    description: "A focused screening call confirms technical context, executive sponsorship, budget fit, governance exposure, and urgency.",
    details: "Go / no-go decision for the CAIO Diagnostic.",
    status: "Vetting"
  },
  {
    step: "STEP 3",
    title: "Engagement Launch",
    description: "Once accepted, engagement documents are completed, access boundaries are defined, and the diagnostic review begins.",
    details: "Principal-led review, architecture assessment, governance map, and executive roadmap.",
    status: "Launch"
  }
];
